type ModelRef = { id: string; handle: string };

type PageModels<M extends ModelRef = ModelRef> = {
  models?: M[] | null;
} | null | undefined;

type PageDataForAccessor<M extends ModelRef = ModelRef> = {
  models?: M[] | null;
  fields?: Array<{
    modelId: string;
    handle: string;
    relationshipLink?: { targetModelId?: string } | null;
  }> | null;
} | null | undefined;

type BlockForAccessor = { modelId: string };

type SpreadsheetFieldLite = {
  modelId: string;
  handle: string;
  relationshipLink?: { cardinality?: string } | null;
};

/** Resolve a spreadsheet column's model tag (handle or id) against page models. */
export const resolveSpreadsheetColumnModel = <M extends ModelRef>(
  columnModel: string | undefined,
  pageData?: PageModels<M>,
): M | null => {
  if (!columnModel || !pageData?.models) return null;
  return (
    pageData.models.find(
      (m) => m.handle === columnModel || m.id === columnModel,
    ) ?? null
  );
};

/**
 * Resolve the field definition for a spreadsheet column, scoped by model so
 * shared handles (e.g. status/type) across models bind the correct options.
 */
export const resolveSpreadsheetColumnField = <T extends SpreadsheetFieldLite>(
  args: {
    fieldHandle: string;
    columnModel: ModelRef | null;
    blockModelId: string;
    fields: T[];
  },
): T | undefined => {
  const { fieldHandle, columnModel, blockModelId, fields } = args;
  if (fieldHandle === "id" && columnModel) {
    return fields.find(
      (f) =>
        f.modelId === blockModelId &&
        f.handle === columnModel.handle &&
        f.relationshipLink?.cardinality === "MANY_TO_ONE",
    );
  }
  const fieldModelId = columnModel?.id ?? blockModelId;
  return fields.find(
    (f) => f.modelId === fieldModelId && f.handle === fieldHandle,
  );
};

// Resolve accessor path for a column. If the column targets a related model,
// build `${sourceHandle}.${fieldHandle}` using relationship links from pageData.
export const resolveAccessorPath = <M extends ModelRef>(
  block: BlockForAccessor,
  columnFieldHandle: string,
  columnModelHandle: string | undefined,
  pageData?: PageDataForAccessor<M>,
): string => {
  if (!pageData) return columnFieldHandle;

  // If no model override, or the model matches this block's model, use flat handle
  if (!columnModelHandle) return columnFieldHandle;

  const relatedModel = resolveSpreadsheetColumnModel(
    columnModelHandle,
    pageData,
  );
  if (!relatedModel) return columnFieldHandle;

  // If related model is same as block model, no nesting
  if (relatedModel.id === block.modelId) return columnFieldHandle;

  // Find relationship field on the block's model that links to relatedModel
  const sourceRelField = pageData.fields?.find(
    (f) =>
      f.modelId === block.modelId &&
      f.relationshipLink?.targetModelId === relatedModel.id,
  );

  if (sourceRelField?.handle) {
    return `${sourceRelField.handle}.${columnFieldHandle}`;
  }

  return columnFieldHandle;
};
